import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { Button, Card, Input, Select, Modal } from '../../components/ui';
import { TemplateEditor, DestinatariBuilder, AVAILABLE_TAGS } from '../../components/notifiche';
import api from '../../services/api';

// Step del wizard
const STEPS = [
  { id: 1, label: 'Contesto', description: 'Seleziona il contesto della notifica' },
  { id: 2, label: 'Template', description: 'Scegli o crea il template email' },
  { id: 3, label: 'Destinatari', description: 'Configura i destinatari' },
  { id: 4, label: 'Anteprima', description: 'Rivedi e invia' }
];

// Tipi di contesto
const CONTESTI = [
  {
    value: 'scadenza',
    label: 'Scadenza Manutenzione',
    icon: '📅',
    description: 'Notifica relativa a una scadenza programmata'
  },
  {
    value: 'contratto',
    label: 'Contratto',
    icon: '📋',
    description: 'Notifica relativa a un contratto fornitore'
  },
  {
    value: 'intervento',
    label: 'Intervento',
    icon: '🔧',
    description: 'Notifica relativa a un intervento registrato'
  },
  {
    value: 'generico',
    label: 'Comunicazione Generica',
    icon: '✉️',
    description: 'Email libera senza riferimento a entità specifiche'
  }
];

function InvioManualePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();

  // Step corrente
  const [currentStep, setCurrentStep] = useState(1);

  // Form data
  const [formData, setFormData] = useState({
    // Step 1 - Contesto
    contesto: '',
    entitaId: '',
    entitaNome: '',

    // Step 2 - Template
    templateId: '',
    useCustom: false,
    oggetto: '',
    corpo: '',

    // Step 3 - Destinatari
    destinatari: { tipo: '', ruoli: [], emailCustom: [], cc: [], ccRuoli: [] },

    // Preview data
    previewContext: {}
  });

  // Dati caricati
  const [templates, setTemplates] = useState([]);
  const [scadenze, setScadenze] = useState([]);
  const [contratti, setContratti] = useState([]);
  const [interventi, setInterventi] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [rsa, setRsa] = useState([]);

  // Stati
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [resolvedDestinatari, setResolvedDestinatari] = useState([]);

  // Carica dati iniziali
  useEffect(() => {
    loadInitialData();

    // Precompila da query params
    const tipo = searchParams.get('tipo');
    const id = searchParams.get('id');
    if (tipo && id) {
      setFormData(prev => ({
        ...prev,
        contesto: tipo,
        entitaId: id
      }));
    }
  }, []);

  // Carica entità quando cambia contesto
  useEffect(() => {
    if (formData.contesto && formData.contesto !== 'generico') {
      loadEntita();
    }
  }, [formData.contesto]);

  // Carica template filtrati per contesto
  useEffect(() => {
    loadTemplates();
  }, [formData.contesto]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [fornitoriData, rsaData] = await Promise.all([
        api.get('/api/fornitori'),
        api.get('/api/rsa')
      ]);
      setFornitori(fornitoriData.data.items || []);
      setRsa(rsaData.data.items || []);
    } catch (error) {
      console.error('Errore caricamento dati:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const tipologia = formData.contesto === 'generico' ? 'manuale' : formData.contesto;
      const response = await api.get(`/api/notifiche/templates?tipologia=${tipologia}`);
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Errore caricamento templates:', error);
    }
  };

  const loadEntita = async () => {
    try {
      if (formData.contesto === 'scadenza') {
        const response = await api.get('/api/scadenze?stato=programmata,in_scadenza&limit=100');
        setScadenze(response.data.items || []);
      } else if (formData.contesto === 'contratto') {
        const response = await api.get('/api/contratti?limit=100');
        setContratti(response.data.items || []);
      } else if (formData.contesto === 'intervento') {
        const response = await api.get('/api/interventi?limit=100');
        setInterventi(response.data.items || []);
      }
    } catch (error) {
      console.error('Errore caricamento entità:', error);
    }
  };

  // Gestione step
  const nextStep = () => {
    if (validateCurrentStep()) {
      if (currentStep === 3) {
        // Prima di andare all'anteprima, risolvi destinatari e genera preview
        generatePreview();
      }
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.contesto) {
          showToast('Seleziona un contesto', 'error');
          return false;
        }
        if (formData.contesto !== 'generico' && !formData.entitaId) {
          showToast('Seleziona un\'entità', 'error');
          return false;
        }
        return true;

      case 2:
        if (!formData.useCustom && !formData.templateId) {
          showToast('Seleziona un template o usa email personalizzata', 'error');
          return false;
        }
        if (formData.useCustom && (!formData.oggetto || !formData.corpo)) {
          showToast('Compila oggetto e corpo dell\'email', 'error');
          return false;
        }
        return true;

      case 3:
        if (!formData.destinatari?.tipo && formData.destinatari?.emailCustom?.length === 0) {
          showToast('Configura almeno un destinatario', 'error');
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  // Genera preview
  const generatePreview = async () => {
    try {
      // Risolvi destinatari
      const destResponse = await api.post('/api/notifiche/destinatari/risolvi', {
        ...formData.destinatari,
        scadenzaId: formData.contesto === 'scadenza' ? formData.entitaId : null,
        contrattoId: formData.contesto === 'contratto' ? formData.entitaId : null,
        interventoId: formData.contesto === 'intervento' ? formData.entitaId : null
      });
      setResolvedDestinatari(destResponse.data.data || []);

      // Genera preview email
      const template = formData.useCustom
        ? { oggetto: formData.oggetto, corpo: formData.corpo }
        : templates.find(t => t.id === formData.templateId);

      if (template) {
        const previewResponse = await api.post('/api/notifiche/preview', {
          templateId: formData.templateId,
          oggetto: template.oggetto,
          corpo: template.corpo,
          contesto: formData.contesto,
          entitaId: formData.entitaId
        });
        setPreviewData(previewResponse.data.data);
      }
    } catch (error) {
      console.error('Errore generazione preview:', error);
      showToast('Errore generazione anteprima', 'error');
    }
  };

  // Invio email
  const handleSend = async () => {
    setSending(true);
    try {
      const template = formData.useCustom
        ? { oggetto: formData.oggetto, corpo: formData.corpo }
        : templates.find(t => t.id === formData.templateId);

      await api.post('/api/notifiche/invia', {
        templateId: formData.useCustom ? null : formData.templateId,
        oggetto: template?.oggetto,
        corpo: template?.corpo,
        destinatari: resolvedDestinatari.map(d => d.email),
        cc: formData.destinatari.cc,
        contesto: formData.contesto,
        entitaId: formData.entitaId,
        entitaTipo: formData.contesto
      });

      showToast(`Email inviata a ${resolvedDestinatari.length} destinatari`, 'success');
      navigate('/admin/notifiche');
    } catch (error) {
      console.error('Errore invio:', error);
      showToast(error.response?.data?.error || 'Errore durante l\'invio', 'error');
    } finally {
      setSending(false);
    }
  };

  // Handlers
  const handleContestoChange = (contesto) => {
    setFormData(prev => ({
      ...prev,
      contesto,
      entitaId: '',
      entitaNome: '',
      templateId: ''
    }));
  };

  const handleEntitaChange = (e) => {
    const id = e.target.value;
    let nome = '';

    if (formData.contesto === 'scadenza') {
      const scadenza = scadenze.find(s => s.id === id);
      nome = scadenza ? `${scadenza.tipologiaIntervento} - ${scadenza.rsaNome}` : '';
    } else if (formData.contesto === 'contratto') {
      const contratto = contratti.find(c => c.id === id);
      nome = contratto?.nome || '';
    } else if (formData.contesto === 'intervento') {
      const intervento = interventi.find(i => i.id === id);
      nome = intervento ? `${intervento.tipologia} - ${intervento.rsaNome}` : '';
    }

    setFormData(prev => ({
      ...prev,
      entitaId: id,
      entitaNome: nome
    }));
  };

  const handleTemplateSelect = (templateId) => {
    if (templateId) {
      const template = templates.find(t => t.id === templateId);
      setFormData(prev => ({
        ...prev,
        templateId,
        useCustom: false,
        oggetto: template?.oggetto || '',
        corpo: template?.corpo || ''
      }));
    }
  };

  // Opzioni entità
  const getEntitaOptions = () => {
    if (formData.contesto === 'scadenza') {
      return scadenze.map(s => ({
        value: s.id,
        label: `${s.tipologiaIntervento || 'N/D'} - ${s.rsaNome || 'N/D'} (${new Date(s.dataScadenza).toLocaleDateString('it-IT')})`
      }));
    } else if (formData.contesto === 'contratto') {
      return contratti.map(c => ({
        value: c.id,
        label: `${c.nome} - ${c.fornitoreNome || 'N/D'}`
      }));
    } else if (formData.contesto === 'intervento') {
      return interventi.map(i => ({
        value: i.id,
        label: `${i.tipologia || 'N/D'} - ${i.rsaNome || 'N/D'} (${new Date(i.data).toLocaleDateString('it-IT')})`
      }));
    }
    return [];
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <a href="/admin" className="hover:text-primary-600">Admin</a>
          <span>/</span>
          <a href="/admin/notifiche" className="hover:text-primary-600">Notifiche</a>
          <span>/</span>
          <span>Invio Manuale</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Invia Notifica Email</h1>
        <p className="text-gray-500 mt-1">
          Wizard guidato per l'invio di notifiche email
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {STEPS.map((step, idx) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center ${idx > 0 ? 'ml-4' : ''}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  currentStep > step.id
                    ? 'bg-green-500 text-white'
                    : currentStep === step.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {currentStep > step.id ? '✓' : step.id}
                </div>
                <div className="ml-3 hidden md:block">
                  <div className={`text-sm font-medium ${
                    currentStep >= step.id ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    {step.label}
                  </div>
                  <div className="text-xs text-gray-500">{step.description}</div>
                </div>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`w-16 h-0.5 mx-4 ${
                  currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <Card className="p-6">
        {/* STEP 1: Contesto */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Seleziona il contesto</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {CONTESTI.map(contesto => (
                <button
                  key={contesto.value}
                  type="button"
                  onClick={() => handleContestoChange(contesto.value)}
                  className={`text-left p-4 rounded-lg border-2 transition-all ${
                    formData.contesto === contesto.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{contesto.icon}</span>
                    <div>
                      <div className="font-semibold text-gray-900">{contesto.label}</div>
                      <div className="text-sm text-gray-500">{contesto.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Selezione entità specifica */}
            {formData.contesto && formData.contesto !== 'generico' && (
              <div className="pt-4 border-t">
                <Select
                  label={`Seleziona ${formData.contesto}`}
                  value={formData.entitaId}
                  onChange={handleEntitaChange}
                  options={getEntitaOptions()}
                  placeholder={`Scegli ${formData.contesto}...`}
                />
              </div>
            )}
          </div>
        )}

        {/* STEP 2: Template */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Scegli il template</h2>

            {/* Toggle personalizzato */}
            <div className="flex items-center gap-4 pb-4 border-b">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.useCustom}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    useCustom: e.target.checked,
                    templateId: e.target.checked ? '' : prev.templateId
                  }))}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                />
                <span className="text-sm font-medium text-gray-700">
                  Usa email personalizzata (senza template)
                </span>
              </label>
            </div>

            {!formData.useCustom ? (
              /* Selezione template */
              <div className="space-y-4">
                {templates.length > 0 ? (
                  <div className="grid gap-3">
                    {templates.map(template => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => handleTemplateSelect(template.id)}
                        className={`text-left p-4 rounded-lg border-2 transition-all ${
                          formData.templateId === template.id
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-semibold text-gray-900">{template.nome}</div>
                            <div className="text-sm text-gray-500 mt-1">{template.oggetto}</div>
                          </div>
                          <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                            {template.codice}
                          </code>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>Nessun template disponibile per questo contesto.</p>
                    <Button
                      variant="link"
                      onClick={() => setFormData(prev => ({ ...prev, useCustom: true }))}
                      className="mt-2"
                    >
                      Usa email personalizzata
                    </Button>
                  </div>
                )}

                {/* Anteprima template selezionato */}
                {formData.templateId && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Anteprima contenuto:</h4>
                    <div className="text-sm text-gray-600 whitespace-pre-wrap max-h-40 overflow-y-auto">
                      {templates.find(t => t.id === formData.templateId)?.corpo}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Editor personalizzato */
              <div className="space-y-4">
                <Input
                  label="Oggetto Email *"
                  value={formData.oggetto}
                  onChange={(e) => setFormData(prev => ({ ...prev, oggetto: e.target.value }))}
                  placeholder="Es: Comunicazione importante da Peralba Health Care"
                />

                <TemplateEditor
                  label="Corpo Email *"
                  value={formData.corpo}
                  onChange={(corpo) => setFormData(prev => ({ ...prev, corpo }))}
                  rows={12}
                  showPreview={true}
                  previewData={{
                    fornitore: 'Fornitore Esempio',
                    rsa: 'RSA Villa Serena'
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* STEP 3: Destinatari */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Configura i destinatari</h2>

            <DestinatariBuilder
              value={formData.destinatari}
              onChange={(destinatari) => setFormData(prev => ({ ...prev, destinatari }))}
              contextType={formData.contesto}
              showCC={true}
            />
          </div>
        )}

        {/* STEP 4: Anteprima e Invio */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Rivedi e Invia</h2>

            {/* Riepilogo */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Contesto</h4>
                <p className="text-gray-900">
                  {CONTESTI.find(c => c.value === formData.contesto)?.label}
                </p>
                {formData.entitaNome && (
                  <p className="text-sm text-gray-500 mt-1">{formData.entitaNome}</p>
                )}
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Destinatari</h4>
                <p className="text-gray-900">{resolvedDestinatari.length} destinatari</p>
                <div className="text-sm text-gray-500 mt-1 max-h-20 overflow-y-auto">
                  {resolvedDestinatari.slice(0, 3).map((d, i) => (
                    <div key={i}>{d.nome || d.email}</div>
                  ))}
                  {resolvedDestinatari.length > 3 && (
                    <div>...e altri {resolvedDestinatari.length - 3}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Anteprima Email */}
            <div className="border rounded-lg overflow-hidden">
              <div className="p-4 bg-gray-50 border-b">
                <h4 className="text-sm font-semibold text-gray-700">Anteprima Email</h4>
              </div>

              <div className="p-4 space-y-3">
                <div className="flex gap-3">
                  <span className="text-sm text-gray-500 w-20">Da:</span>
                  <span className="text-sm font-medium">automation@peralbahc.com</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-sm text-gray-500 w-20">A:</span>
                  <span className="text-sm">
                    {resolvedDestinatari.slice(0, 2).map(d => d.email).join(', ')}
                    {resolvedDestinatari.length > 2 && ` +${resolvedDestinatari.length - 2} altri`}
                  </span>
                </div>
                {formData.destinatari.cc?.length > 0 && (
                  <div className="flex gap-3">
                    <span className="text-sm text-gray-500 w-20">CC:</span>
                    <span className="text-sm">{formData.destinatari.cc.join(', ')}</span>
                  </div>
                )}
                <div className="flex gap-3">
                  <span className="text-sm text-gray-500 w-20">Oggetto:</span>
                  <span className="text-sm font-medium">{previewData?.oggetto || formData.oggetto}</span>
                </div>
              </div>

              <div className="p-4 border-t bg-white">
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                  {previewData?.corpo || formData.corpo}
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-3">
                <span className="text-xl">⚠️</span>
                <div>
                  <p className="font-medium text-amber-800">Conferma invio</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Stai per inviare questa email a <strong>{resolvedDestinatari.length}</strong> destinatari.
                    L'operazione non può essere annullata.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t">
          <Button
            variant="ghost"
            onClick={() => currentStep === 1 ? navigate('/admin/notifiche') : prevStep()}
          >
            {currentStep === 1 ? 'Annulla' : '← Indietro'}
          </Button>

          <div className="flex gap-3">
            {currentStep < 4 ? (
              <Button variant="primary" onClick={nextStep}>
                Avanti →
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleSend}
                disabled={sending || resolvedDestinatari.length === 0}
              >
                {sending ? '📤 Invio in corso...' : '📤 Invia Email'}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default InvioManualePage;
